import { useState, useEffect, useCallback } from 'react'
import { useCurrentUser } from './useCurrentUser'
import { useTables } from './useBackend'
import type { Table, User, Character, Member } from '@/types'

/**
 * Hook personalizado para gerenciar os detalhes de uma mesa
 * Provê funções para carregar os detalhes da mesa e o estado de carregamento
 * 
 * NOTA: Removido reload automático no focus pois causava re-renders 
 * indesejados que fechavam modais (ex: upload de mídia)
 */
export function useTableDetails(tableId: string | null, initialUser?: User) {
  const [table, setTable] = useState<Table | null>(null)
  const [characters, setCharacters] = useState<Character[]>([])
  const [members, setMembers] = useState<Member[]>([])
  const [isLoading, setIsLoading] = useState(true)
  
  const { getTableDetails } = useTables()
  const { user, loadUser } = useCurrentUser()
  
  // Função para carregar detalhes da mesa
  const loadTableDetails = useCallback(async () => {
    if (!tableId) {
      console.error("❌ useTableDetails - Tentativa de carregar detalhes sem ID");
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true)
      const backendTable = await getTableDetails(tableId)
      
      // Mapear dados do backend para frontend
      const mappedTable: Table = {
        id: backendTable.id,
        name: backendTable.name,
        description: backendTable.description,
        subject: backendTable.subject,
        creator_id: backendTable.creator_id,
        created_at: backendTable.created_at,
        image_url: backendTable.image_url,
        is_active: backendTable.is_active,
        character_creation_open: backendTable.character_creation_open ?? true,
        member_count: backendTable.members?.length || 0,
        character_count: backendTable.characters?.length || 0
      }
      
      setTable(mappedTable)
      setCharacters(backendTable.characters || [])
      setMembers(backendTable.members || [])
      
      // Sempre recarregar o usuário atual ao carregar a mesa
      await loadUser()
      
      return { mappedTable, characters: backendTable.characters || [], members: backendTable.members || [] }
    } catch (error) {
      console.error('Erro ao carregar detalhes da mesa:', error)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [tableId, getTableDetails, loadUser])
  
  // Carregar detalhes da mesa apenas ao iniciar ou quando tableId mudar
  useEffect(() => {
    loadTableDetails()
  }, [loadTableDetails])
  
  return { 
    table, 
    setTable, 
    characters, 
    setCharacters, 
    members, 
    setMembers, 
    isLoading, 
    loadTableDetails,
    currentUser: user || initialUser
  }
}